import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiEndpoint } from '@/api/registry';
import { useApiQuery } from '@/api/useApiQuery';
import { toModelLeadtimeSpans, type FctModelConfig } from '@/features/forecasts/config/fctRuns';
import type { ModelOutputRecord } from '@/features/forecasts/forecasts.api.types';
import { useForecastQueryState } from '@/features/forecasts/hooks/useForecastQueryState';
import { useForecastRunOutputs } from '@/features/forecasts/hooks/useForecastRunOutputs';
import { useTenant } from '@/providers/TenantProvider';

export type FctForecast = {
  fct: string;
  forecast: ModelOutputRecord[];
};

type ForecastSolutionRecord = { solution_id?: string };
type ForecastSolutionsListResponse = { forecast_solutions?: ForecastSolutionRecord[] };

// Turn the source-of-truth lead_time / span filter values into LTSP pairs.
export function selectedLeadtimeSpans(leadTime?: string, span?: string): Array<[number, number]> {
  const lt = Number(leadTime);
  const sp = Number(span);
  if (!Number.isFinite(lt) || !Number.isFinite(sp)) return [];
  return [[lt, sp]];
}

function useSharedPrimaryKey() {
  const { grains } = useTenant();
  const { state } = useForecastQueryState();
  return useMemo(
    () => ({
      ...(grains.product && state.product ? { [grains.product]: state.product } : {}),
      ...(grains.ship_to && state.customer ? { [grains.ship_to]: state.customer } : {}),
      ...(grains.ship_to_geography ? { [grains.ship_to_geography]: state.shipToGeography || 'North America' } : {}),
    }),
    [grains.product, grains.ship_to, grains.ship_to_geography, state.product, state.customer, state.shipToGeography],
  );
}

// Resolves one (fct, model) production solution, then fetches its run + outputs.
// Renders nothing — it reports {forecast, isLoading} up through onResult so the
// parent can collect a dynamic number of fcts without violating hook order.
function FctModelForecastProbe({
  fct,
  config,
  ltsp,
  primaryKey,
  onResult,
}: {
  fct: string;
  config: FctModelConfig;
  ltsp: Array<[number, number]>;
  primaryKey: Record<string, string>;
  onResult: (fct: string, forecast: ModelOutputRecord[], isLoading: boolean, isError: boolean) => void;
}) {
  const { tenantAlias } = useTenant();

  const solutionsResult = useApiQuery<ForecastSolutionsListResponse>(tenantAlias, ApiEndpoint.SolutionsForecastList, {
    query: {
      fct,
      model_name: config.modelName,
      model_version: config.modelVersion,
      status: 'production',
      limit: 1,
    },
  });
  const solutionId = solutionsResult.data?.forecast_solutions?.[0]?.solution_id ?? null;

  const {
    forecast,
    isLoading: runLoading,
    isError: runError,
  } = useForecastRunOutputs({
    solutionId,
    primaryKey,
    leadtimeSpans: toModelLeadtimeSpans(config, ltsp),
    enabled: Boolean(solutionId),
  });

  const isLoading = solutionsResult.isLoading || (Boolean(solutionId) && runLoading);
  const isError = solutionsResult.isError || runError;

  useEffect(() => {
    onResult(fct, forecast, isLoading, isError);
  }, [fct, forecast, isLoading, isError, onResult]);

  return null;
}

// Fetches the forecast for each selected fct for one model, scoped to the
// current product/customer selection and LTSP. Returns the collector probes to
// render plus the accumulated series.
export function useFctForecasts(config: FctModelConfig) {
  const { state } = useForecastQueryState();
  const primaryKey = useSharedPrimaryKey();
  const fcts = useMemo(() => state.fcts ?? [], [state.fcts]);
  const ltsp = useMemo(() => selectedLeadtimeSpans(state.leadTime, state.span), [state.leadTime, state.span]);

  const [results, setResults] = useState<
    Record<string, { forecast: ModelOutputRecord[]; isLoading: boolean; isError: boolean }>
  >({});

  const onResult = useCallback((fct: string, forecast: ModelOutputRecord[], isLoading: boolean, isError: boolean) => {
    setResults((prev) => {
      const existing = prev[fct];
      if (
        existing &&
        existing.isLoading === isLoading &&
        existing.isError === isError &&
        existing.forecast === forecast
      )
        return prev;
      return { ...prev, [fct]: { forecast, isLoading, isError } };
    });
  }, []);

  const probes = fcts.map((fct) => (
    <FctModelForecastProbe
      key={`${config.modelName}:${config.modelVersion}:${fct}`}
      fct={fct}
      config={config}
      ltsp={ltsp}
      primaryKey={primaryKey}
      onResult={onResult}
    />
  ));

  const series: FctForecast[] = fcts.map((fct) => ({ fct, forecast: results[fct]?.forecast ?? [] }));
  const isLoading = fcts.some((fct) => results[fct]?.isLoading ?? true);
  const isError = fcts.some((fct) => results[fct]?.isError ?? false);

  return { probes, series, isLoading, isError };
}
